// Terrain system for Zodiac Chess tutorials
// Converted from Unity C# terrain generator

interface TerrainTile {
  type: number;
  imageName: string;
  isObstacle?: boolean;
}

interface TutorialTerrain {
  [row: number]: {
    [col: number]: number; // terrain type ID
  };
}

// Terrain type definitions (matching Unity terrain list)
export const TERRAIN_TYPES: { [id: number]: TerrainTile } = {
  0: { type: 0, imageName: 'grasslight.png' },
  1: { type: 1, imageName: 'grassdark.png' },
  2: { type: 2, imageName: 'dirtlight.png' },
  3: { type: 3, imageName: 'dirtdark.png' },
  4: { type: 4, imageName: 'icelight.png' },
  5: { type: 5, imageName: 'icedark.png' },
  6: { type: 6, imageName: 'sandlight.png' },
  7: { type: 7, imageName: 'sanddark.png' },
  8: { type: 8, imageName: 'oceanlight.png' },
  9: { type: 9, imageName: 'oceandark.png' },
  10: { type: 10, imageName: 'lightsandtodarkocean.png' }, // beachligh
  11: { type: 11, imageName: 'darksandtolightocean.png' }, // beachdark
  12: { type: 12, imageName: 'icetograsslight.png' }, // grassedgelight
  13: { type: 13, imageName: 'icetograssdark.png' }, // grassedgedark
  14: { type: 14, imageName: 'grasslightrock.png', isObstacle: true },
  15: { type: 15, imageName: 'grassdarkrock.png', isObstacle: true },
  16: { type: 16, imageName: 'lighticerock.png', isObstacle: true },
  17: { type: 17, imageName: 'icerockdark.png', isObstacle: true },
  18: { type: 18, imageName: 'icetograsslight.png' }, // grasstoicelight
  19: { type: 19, imageName: 'icetograssdark.png' }, // grasstoicedark
  20: { type: 20, imageName: 'dirtlightrock.png', isObstacle: true },
  21: { type: 21, imageName: 'dirtdarkrock.png', isObstacle: true },
  22: { type: 22, imageName: 'lightsandrock.png', isObstacle: true },
  23: { type: 23, imageName: 'darksandrock.png', isObstacle: true },
  24: { type: 24, imageName: 'grassdirtlight.png' }, // grass-to-dirt light
  25: { type: 25, imageName: 'grassdirtdark.png' } // grass-to-dirt dark
};

// Piece ID to constellation mapping (from C# code)
const PIECE_TO_CONSTELLATION: { [pieceId: number]: string } = {
  0: 'Bear',      // Bear
  1: 'Crane',     // Crane
  2: 'Fungi',     // Fungi (shared with Goat tutorials)
  3: 'Frog',      // Frog
  4: 'Goat',      // Goat
  5: 'Fungi',     // Fungi (shared with Goat tutorials)
  6: 'Monkey',    // Monkey
  7: 'Virus',     // Virus
  8: 'Penguin',   // Penguin
  9: 'Rhino',     // Rhino
  10: 'Snake',    // Snake
  11: 'Octopus',  // Octopus
  12: 'Wolf',     // Wolf
};

// Generate checkerboard pattern (default terrain)
function generateCheckerboard(): TutorialTerrain {
  const terrain: TutorialTerrain = {};
  for (let row = 0; row < 8; row++) {
    terrain[row] = {};
    for (let col = 0; col < 5; col++) {
      // Checkerboard pattern: alternating 0 (grasslight) and 1 (grassdark)
      terrain[row][col] = (row + col) % 2;
    }
  }
  return terrain;
}

// Tutorial terrain data converted from C#
export const TUTORIAL_TERRAINS: { [constellation: string]: { [tutorialIndex: number]: TutorialTerrain } } = {
  'Bear': {
    0: generateCheckerboard(), // Level 1-4: Standard checkerboard
    1: generateCheckerboard(),
    2: generateCheckerboard(),
    3: generateCheckerboard(),
    4: { // Level 5: Dirt at top, grass at bottom with transition
      0: { 0: 2, 1: 3, 2: 2, 3: 3, 4: 2 }, // Bottom: dirt terrain
      1: { 0: 3, 1: 2, 2: 3, 3: 2, 4: 3 },
      2: { 0: 2, 1: 3, 2: 2, 3: 3, 4: 2 },
      3: { 0: 3, 1: 2, 2: 3, 3: 2, 4: 3 },
      4: { 0: 24, 1: 25, 2: 24, 3: 25, 4: 24 }, // Grass-to-dirt transition (dirt above, grass below)
      5: { 0: 1, 1: 0, 2: 1, 3: 0, 4: 1 }, // Top: grass checkerboard
      6: { 0: 0, 1: 1, 2: 0, 3: 1, 4: 0 },
      7: { 0: 1, 1: 0, 2: 1, 3: 0, 4: 1 }
    }
  },
  'Crane': {
    0: generateCheckerboard(), // Levels 1-3, 5-6: Standard
    1: generateCheckerboard(),
    2: generateCheckerboard(),
    3: { // Level 4: Mixed with rocks
      0: { 0: 0, 1: 1, 2: 0, 3: 1, 4: 0 },
      1: { 0: 1, 1: 0, 2: 1, 3: 0, 4: 1 },
      2: { 0: 0, 1: 15, 2: 0, 3: 15, 4: 0 }, // Dark grass rocks
      3: { 0: 1, 1: 0, 2: 1, 3: 0, 4: 1 },
      4: { 0: 14, 1: 1, 2: 14, 3: 1, 4: 14 }, // Light grass rocks
      5: { 0: 1, 1: 0, 2: 1, 3: 0, 4: 1 },
      6: { 0: 0, 1: 15, 2: 0, 3: 15, 4: 0 },
      7: { 0: 1, 1: 0, 2: 1, 3: 0, 4: 1 }
    },
    4: generateCheckerboard(),
    5: generateCheckerboard()
  },
  'Frog': {
    0: generateCheckerboard(),
    1: generateCheckerboard(),
    2: generateCheckerboard(),
    3: generateCheckerboard(),
    4: generateCheckerboard()
  },
  'Goat': {
    0: generateCheckerboard(),
    1: generateCheckerboard(),
    2: generateCheckerboard(),
    3: generateCheckerboard()
  },
  'Fungi': {
    0: generateCheckerboard(),
    1: generateCheckerboard(),
    2: generateCheckerboard(),
    3: generateCheckerboard(),
    4: generateCheckerboard(),
    5: generateCheckerboard()
  },
  'Monkey': {
    0: generateCheckerboard(),
    1: generateCheckerboard(),
    2: generateCheckerboard(),
    3: generateCheckerboard(),
    4: generateCheckerboard() // Level 5: Standard grass checkerboard (as per C# code)
  },
  'Octopus': {
    0: generateCheckerboard(),
    1: generateCheckerboard(),
    2: generateCheckerboard(),
    3: generateCheckerboard(),
    4: generateCheckerboard(),
    5: { // Level 6: Single rock terrain feature
      0: { 0: 0, 1: 1, 2: 0, 3: 1, 4: 0 },
      1: { 0: 1, 1: 0, 2: 1, 3: 0, 4: 1 },
      2: { 0: 0, 1: 1, 2: 0, 3: 1, 4: 0 },
      3: { 0: 1, 1: 0, 2: 1, 3: 14, 4: 1 }, // Single light grass rock
      4: { 0: 0, 1: 1, 2: 0, 3: 1, 4: 0 },
      5: { 0: 1, 1: 0, 2: 1, 3: 0, 4: 1 },
      6: { 0: 0, 1: 1, 2: 0, 3: 1, 4: 0 },
      7: { 0: 1, 1: 0, 2: 1, 3: 0, 4: 1 }
    }
  },
  'Rhino': {
    0: generateCheckerboard(),
    1: generateCheckerboard(),
    2: generateCheckerboard(),
    3: generateCheckerboard(),
    4: { // Level 5: Full dirt terrain
      0: { 0: 2, 1: 3, 2: 2, 3: 3, 4: 2 },
      1: { 0: 3, 1: 2, 2: 3, 3: 2, 4: 3 },
      2: { 0: 2, 1: 3, 2: 2, 3: 3, 4: 2 },
      3: { 0: 3, 1: 2, 2: 3, 3: 2, 4: 3 },
      4: { 0: 2, 1: 3, 2: 2, 3: 3, 4: 2 },
      5: { 0: 3, 1: 2, 2: 3, 3: 2, 4: 3 },
      6: { 0: 2, 1: 3, 2: 2, 3: 3, 4: 2 },
      7: { 0: 3, 1: 2, 2: 3, 3: 2, 4: 3 }
    }
  },
  'Snake': {
    0: generateCheckerboard(),
    1: generateCheckerboard(),
    2: generateCheckerboard(),
    3: generateCheckerboard()
  },
  'Penguin': { // Ice terrain
    0: { // Full ice terrain
      0: { 0: 4, 1: 5, 2: 4, 3: 5, 4: 4 },
      1: { 0: 5, 1: 4, 2: 5, 3: 4, 4: 5 },
      2: { 0: 4, 1: 5, 2: 4, 3: 5, 4: 4 },
      3: { 0: 5, 1: 4, 2: 5, 3: 4, 4: 5 },
      4: { 0: 4, 1: 5, 2: 4, 3: 5, 4: 4 },
      5: { 0: 5, 1: 4, 2: 5, 3: 4, 4: 5 },
      6: { 0: 4, 1: 5, 2: 4, 3: 5, 4: 4 },
      7: { 0: 5, 1: 4, 2: 5, 3: 4, 4: 5 }
    },
    1: { // Full ice terrain
      0: { 0: 4, 1: 5, 2: 4, 3: 5, 4: 4 },
      1: { 0: 5, 1: 4, 2: 5, 3: 4, 4: 5 },
      2: { 0: 4, 1: 5, 2: 4, 3: 5, 4: 4 },
      3: { 0: 5, 1: 4, 2: 5, 3: 4, 4: 5 },
      4: { 0: 4, 1: 5, 2: 4, 3: 5, 4: 4 },
      5: { 0: 5, 1: 4, 2: 5, 3: 4, 4: 5 },
      6: { 0: 4, 1: 5, 2: 4, 3: 5, 4: 4 },
      7: { 0: 5, 1: 4, 2: 5, 3: 4, 4: 5 }
    },
    2: generateCheckerboard(),
    3: generateCheckerboard()
  },
  'Wolf': {
    0: generateCheckerboard(),
    1: generateCheckerboard(),
    2: generateCheckerboard(),
    3: { // Level 4: Mixed terrain with transitions
      0: { 0: 0, 1: 1, 2: 0, 3: 1, 4: 0 },
      1: { 0: 1, 1: 0, 2: 1, 3: 0, 4: 1 },
      2: { 0: 0, 1: 1, 2: 0, 3: 1, 4: 0 },
      3: { 0: 13, 1: 12, 2: 13, 3: 12, 4: 13 }, // Ice-grass transition
      4: { 0: 2, 1: 3, 2: 2, 3: 3, 4: 2 }, // Dirt terrain
      5: { 0: 3, 1: 2, 2: 3, 3: 2, 4: 3 },
      6: { 0: 2, 1: 3, 2: 2, 3: 3, 4: 2 },
      7: { 0: 3, 1: 2, 2: 3, 3: 2, 4: 3 }
    },
    4: generateCheckerboard()
  },
  'Tree': {
    0: { // Tree Tutorial 1: Bears are at row 2, cols 2&3 - put overlay rocks there
      0: { 0: 0, 1: 1, 2: 0, 3: 1, 4: 0 },
      1: { 0: 1, 1: 0, 2: 1, 3: 0, 4: 1 },
      2: { 0: 0, 1: 1, 2: 0, 3: 1, 4: 0 }, // Overlay rocks at Bear positions (row 2)
      3: { 0: 1, 1: 0, 2: 1, 3: 0, 4: 1 },
      4: { 0: 0, 1: 1, 2: 0, 3: 1, 4: 0 },
      5: { 0: 1, 1: 0, 2: 1, 3: 0, 4: 1 },
      6: { 0: 0, 1: 1, 2: 15, 3: 1, 4: 0 },
      7: { 0: 1, 1: 0, 2: 14, 3: 0, 4: 1 }
    },
    1: generateCheckerboard(),
    2: generateCheckerboard()
  },
  'Virus': {
    0: generateCheckerboard(),
    1: generateCheckerboard(),
    2: generateCheckerboard()
  }
};

// Get terrain for a specific tutorial
export function getTutorialTerrain(constellation: string, tutorialIndex: number): TutorialTerrain {
  const constellationTerrain = TUTORIAL_TERRAINS[constellation];
  if (!constellationTerrain || !constellationTerrain[tutorialIndex]) {
    return generateCheckerboard(); // Default fallback
  }
  return constellationTerrain[tutorialIndex];
}

// Get terrain image path for a terrain type
export function getTerrainImagePath(terrainType: number): string {
  const terrain = TERRAIN_TYPES[terrainType];
  if (!terrain) {
    return '/images/Terrain images/optimized/grasslight.png'; // Default fallback
  }
  return `/images/Terrain images/optimized/${terrain.imageName}`;
}

// Check if terrain type is an obstacle
export function isTerrainObstacle(terrainType: number): boolean {
  const terrain = TERRAIN_TYPES[terrainType];
  return terrain?.isObstacle || false;
}

// Check if terrain should render as overlay (in front of pieces) for specific tutorials
export function shouldTerrainRenderAsOverlay(constellation: string, tutorialIndex: number, terrainType: number): boolean {
  // Tree Tutorial 1: Rock terrain should render over Bears to make them look like stone obstacles
  if (constellation === 'Tree' && tutorialIndex === 0 && isTerrainObstacle(terrainType)) {
    return true;
  }
  
  return false;
}